import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { AuthShell } from '../components/AuthShell.tsx';
import { ErrorNotice, Field, Spinner } from '../components/ui.tsx';
import { ApiRequestError } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';

export function LoginPage(): React.JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      await login(email, password);
      navigate('/chats', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to LeadGab"
      subtitle="Answer your customers across chat, SMS, email and Messenger."
      footer={
        <>
          No account?{' '}
          <Link to="/register" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            Create one
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error ? <ErrorNotice message={error} /> : null}

        <Field label="Email" htmlFor="email" error={fieldErrors.email}>
          <input
            id="email"
            type="email"
            className={clsx('lg-input', fieldErrors.email && 'lg-input-error')}
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </Field>

        <Field label="Password" htmlFor="password" error={fieldErrors.password}>
          <input
            id="password"
            type="password"
            className={clsx('lg-input', fieldErrors.password && 'lg-input-error')}
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Field>

        <div className="flex justify-end">
          <Link
            to="/password-reset"
            className="text-sm text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
          >
            Forgot password?
          </Link>
        </div>

        <button type="submit" className="lg-btn-primary w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Sign in
        </button>
      </form>
    </AuthShell>
  );
}
